import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Save, Upload, Plus, Edit2, Trash2, FileText, Eye, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';

const DocumentSettings = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [settings, setSettings] = useState({
    company_name: '',
    owner_name: '',
    street: '',
    postal_code: '',
    city: '',
    email: '',
    website: '',
    phone: '',
    logo_url: '',
    bank_name: '',
    iban: '',
    bic: '',
    account_holder: '',
    tax_note: '',
    payment_terms: '',
    thank_you_note: ''
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState('invoice'); // 'invoice', 'delivery', 'credit', 'letter'
  const [realPdfUrl, setRealPdfUrl] = useState(null); // inline PDF preview blob URL
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
  }, []);

  // Revoke blob URL on unmount or when URL changes — prevents memory leaks
  useEffect(() => {
    return () => {
      if (realPdfUrl) window.URL.revokeObjectURL(realPdfUrl);
    };
  }, [realPdfUrl]);

  // Auto-close inline PDF when user switches preview type so it doesn't show the wrong doc type
  useEffect(() => {
    if (realPdfUrl) {
      window.URL.revokeObjectURL(realPdfUrl);
      setRealPdfUrl(null);
    }
  }, [previewType]);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/admin/document-settings');
      // Normalize: the DB may contain BOTH old (street/...) and new (company_street/...)
      // key variants from migrations. Prefer the canonical `company_*` / `bank_*`
      // values used by the PDF generator so the editor always shows what will
      // actually be printed.
      const d = response.data || {};
      setSettings({
        ...d,
        // Mirror canonical → legacy form fields used by the inputs below
        owner_name: d.company_owner || d.owner_name || '',
        street: d.company_street || d.street || '',
        postal_code: d.company_zip || d.postal_code || '',
        city: d.company_city || d.city || '',
        email: d.company_email || d.email || '',
        website: d.company_website || d.website || '',
        phone: d.company_phone || d.phone || '',
        iban: d.bank_iban || d.iban || '',
        bic: d.bank_bic || d.bic || '',
        account_holder: d.bank_account_holder || d.account_holder || '',
      });
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axiosInstance.get('/admin/letter-templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Vorlagen');
    }
  };

  const generateRealPdfPreview = async () => {
    const docType = previewType === 'invoice' ? 'invoice' : previewType === 'delivery' ? 'delivery_note' : null;
    if (!docType) {
      toast.error('Echte PDF-Vorschau aktuell nur für Rechnung & Lieferschein verfügbar');
      return;
    }
    setPdfLoading(true);
    try {
      const res = await axiosInstance.get(`/documents/admin/preview/${docType}`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      // Revoke any previous URL before replacing
      if (realPdfUrl) window.URL.revokeObjectURL(realPdfUrl);
      setRealPdfUrl(blobUrl);
      toast.success('Live-PDF-Vorschau geladen');
    } catch (e) {
      toast.error('PDF-Vorschau konnte nicht erstellt werden');
    } finally {
      setPdfLoading(false);
    }
  };

  const closeInlinePdf = () => {
    if (realPdfUrl) window.URL.revokeObjectURL(realPdfUrl);
    setRealPdfUrl(null);
  };

  const openInlinePdfInNewTab = () => {
    if (realPdfUrl) window.open(realPdfUrl, '_blank');
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      // Mirror legacy form fields to the canonical keys the PDF generator reads.
      // This way edits in the UI are immediately visible on the next PDF.
      const payload = {
        ...settings,
        company_owner: settings.owner_name || '',
        company_street: settings.street || '',
        company_zip: settings.postal_code || '',
        company_city: settings.city || '',
        company_email: settings.email || '',
        company_website: settings.website || '',
        company_phone: settings.phone || '',
        bank_iban: settings.iban || '',
        bank_bic: settings.bic || '',
        bank_account_holder: settings.account_holder || '',
      };
      await axiosInstance.put('/admin/document-settings', payload);
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    try {
      const response = await axiosInstance.post('/admin/document-settings/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSettings({ ...settings, logo_url: response.data.logo_url });
      toast.success('Logo hochgeladen');
    } catch (error) {
      toast.error('Fehler beim Upload');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate({
      name: '',
      subject: '',
      greeting: 'Sehr geehrte Damen und Herren,',
      body: '',
      closing: 'Mit freundlichen Grüßen',
      is_default: false
    });
    setIsEditingTemplate(true);
  };

  const handleSaveTemplate = async () => {
    setLoading(true);
    try {
      if (selectedTemplate.id) {
        await axiosInstance.put(`/admin/letter-templates/${selectedTemplate.id}`, selectedTemplate);
        toast.success('Vorlage aktualisiert');
      } else {
        await axiosInstance.post('/admin/letter-templates', selectedTemplate);
        toast.success('Vorlage erstellt');
      }
      fetchTemplates();
      setIsEditingTemplate(false);
      setSelectedTemplate(null);
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;

    try {
      await axiosInstance.delete(`/admin/letter-templates/${templateId}`);
      toast.success('Vorlage gelöscht');
      fetchTemplates();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const PreviewModal = () => {
    if (!showPreview) return null;

    const getPreviewContent = () => {
      const styles = {
        container: {
          fontFamily: 'Arial, sans-serif',
          padding: '40px',
          backgroundColor: 'white',
          maxWidth: '800px',
          margin: '0 auto',
          minHeight: '1000px',
          boxShadow: '0 0 20px rgba(0,0,0,0.1)'
        },
        header: {
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '40px',
          alignItems: 'flex-start'
        },
        logo: {
          maxWidth: '150px',
          maxHeight: '150px'
        },
        company: {
          lineHeight: '1.6'
        },
        title: {
          fontSize: '28px',
          fontWeight: 'bold',
          color: previewType === 'credit' ? '#D32F2F' : previewType === 'delivery' ? '#2E7D32' : '#6D5A47',
          marginBottom: '20px',
          textAlign: 'right'
        },
        section: {
          marginTop: '30px',
          marginBottom: '20px'
        },
        table: {
          width: '100%',
          borderCollapse: 'collapse',
          marginTop: '20px'
        },
        th: {
          backgroundColor: '#C4A084',
          color: 'white',
          padding: '12px',
          textAlign: 'left'
        },
        td: {
          padding: '10px',
          borderBottom: '1px solid #eee'
        },
        footer: {
          marginTop: '40px',
          paddingTop: '20px',
          borderTop: '2px solid #C4A084',
          fontSize: '12px',
          color: '#666'
        }
      };

      return (
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.header}>
            <div>
              {settings.logo_url && (
                <img src={settings.logo_url} alt="Logo" style={styles.logo} />
              )}
              <div style={styles.company}>
                <strong>{settings.company_name || 'Firmenname'}</strong><br />
                {settings.owner_name || 'Inhabername'}<br />
                {settings.street || 'Straße'}<br />
                {settings.postal_code || 'PLZ'} {settings.city || 'Stadt'}<br />
                {settings.email && <>{settings.email}<br /></>}
                {settings.website && <>{settings.website}<br /></>}
                {settings.phone && <>Tel: {settings.phone}</>}
              </div>
            </div>
            <div style={styles.title}>
              {previewType === 'invoice' && 'RECHNUNG'}
              {previewType === 'delivery' && 'LIEFERSCHEIN'}
              {previewType === 'credit' && 'GUTSCHRIFT'}
              {previewType === 'letter' && 'GESCHÄFTSBRIEF'}
            </div>
          </div>

          {/* Document Info */}
          {previewType !== 'letter' && (
            <div style={styles.section}>
              <strong>Rechnungsadresse:</strong><br />
              Max Mustermann<br />
              Musterstraße 123<br />
              12345 Musterstadt
            </div>
          )}

          <div style={styles.section}>
            <strong>{previewType === 'invoice' ? 'Rechnungs-Nr.:' : previewType === 'delivery' ? 'Lieferschein-Nr.:' : previewType === 'credit' ? 'Gutschrift-Nr.:' : 'Datum:'}</strong> {previewType === 'letter' ? '' : 'HYS-2025-00001'}<br />
            <strong>Datum:</strong> {new Date().toLocaleDateString('de-DE')}
          </div>

          {/* Items Table */}
          {previewType !== 'letter' && (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Artikel</th>
                  <th style={styles.th}>Menge</th>
                  {previewType !== 'delivery' && <th style={styles.th}>Preis</th>}
                  {previewType !== 'delivery' && <th style={styles.th}>Gesamt</th>}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={styles.td}>Beispiel Produkt</td>
                  <td style={styles.td}>2</td>
                  {previewType !== 'delivery' && <td style={styles.td}>25,00€</td>}
                  {previewType !== 'delivery' && <td style={styles.td}>50,00€</td>}
                </tr>
                <tr>
                  <td style={styles.td}>Weiteres Produkt</td>
                  <td style={styles.td}>1</td>
                  {previewType !== 'delivery' && <td style={styles.td}>15,99€</td>}
                  {previewType !== 'delivery' && <td style={styles.td}>15,99€</td>}
                </tr>
              </tbody>
            </table>
          )}

          {/* Letter Content */}
          {previewType === 'letter' && selectedTemplate && (
            <div style={styles.section}>
              <div style={{ marginBottom: '30px' }}>
                <strong>Empfänger:</strong><br />
                Max Mustermann<br />
                Musterstraße 123<br />
                12345 Musterstadt
              </div>
              
              {selectedTemplate.subject && (
                <div style={{ marginBottom: '20px' }}>
                  <strong>{selectedTemplate.subject}</strong>
                </div>
              )}
              
              <p>{selectedTemplate.greeting}</p>
              
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '20px', marginBottom: '20px' }}>
                {selectedTemplate.body || 'Brieftext...'}
              </div>
              
              <p style={{ marginTop: '30px' }}>{selectedTemplate.closing}</p>
              <p style={{ marginTop: '40px' }}>
                {settings.owner_name}<br />
                {settings.company_name}
              </p>
            </div>
          )}

          {/* Total */}
          {previewType !== 'letter' && previewType !== 'delivery' && (
            <div style={{ textAlign: 'right', marginTop: '20px', fontSize: '18px' }}>
              <strong>Gesamtsumme: 65,99€</strong>
            </div>
          )}

          {/* Footer */}
          <div style={styles.footer}>
            {settings.tax_note && <p>{settings.tax_note}</p>}
            {previewType === 'invoice' && settings.payment_terms && (
              <p style={{ marginTop: '10px' }}>{settings.payment_terms}</p>
            )}
            {settings.bank_name && previewType === 'invoice' && (
              <div style={{ marginTop: '15px' }}>
                <strong>Bankverbindung:</strong><br />
                {settings.bank_name}<br />
                {settings.iban && <>IBAN: {settings.iban}<br /></>}
                {settings.bic && <>BIC: {settings.bic}<br /></>}
                {settings.account_holder && <>Kontoinhaber: {settings.account_holder}</>}
              </div>
            )}
            {settings.thank_you_note && (
              <p style={{ marginTop: '20px', fontStyle: 'italic' }}>{settings.thank_you_note}</p>
            )}
          </div>
        </div>
      );
    };

    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 pointer-events-none"
        onClick={() => setShowPreview(false)}
      >
        <div 
          className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-auto pointer-events-auto relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
            <h2 className="text-lg sm:text-xl font-bold">
              Vorschau: {
                previewType === 'invoice' ? 'Rechnung' :
                previewType === 'delivery' ? 'Lieferschein' :
                previewType === 'credit' ? 'Gutschrift' :
                'Brief'
              }
            </h2>
            <div className="flex gap-2 flex-wrap">
              <div className="hidden sm:flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewType('invoice')}
                >
                  Rechnung
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewType('delivery')}
                >
                  Lieferschein
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewType('credit')}
                >
                  Gutschrift
                </Button>
                {selectedTemplate && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewType('letter')}
                  >
                    Brief
                  </Button>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={generateRealPdfPreview}
                disabled={pdfLoading || previewType === 'credit' || previewType === 'letter'}
                title={previewType === 'invoice' || previewType === 'delivery'
                  ? 'Echte PDF-Vorschau direkt hier im Editor einblenden'
                  : 'PDF-Vorschau aktuell nur für Rechnung & Lieferschein'}
                className="border-[#C4A084] text-[#5D4E3C] hover:bg-[#E8DCC8]"
                data-testid="docsettings-real-pdf-preview"
              >
                <FileText className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">{pdfLoading ? 'Lade PDF…' : 'Live-PDF anzeigen'}</span>
                <span className="sm:hidden">{pdfLoading ? '…' : 'PDF'}</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowPreview(false)}
                className="flex items-center gap-1"
              >
                <X className="h-4 w-4" />
                <span className="hidden sm:inline">Schließen</span>
              </Button>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            {realPdfUrl ? (
              <div className="space-y-3" data-testid="docsettings-inline-pdf">
                <div className="flex items-center justify-between gap-2 flex-wrap p-3 bg-[#FAF5EE] border border-[#E8DCC8] rounded-none">
                  <p className="text-sm text-[#5D4E3C] font-medium">
                    Live-PDF-Vorschau (auf Basis der aktuellen Einstellungen)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={openInlinePdfInNewTab}
                      className="rounded-none border-[#C4A084] text-[#5D4E3C]"
                      data-testid="docsettings-pdf-newtab"
                    >
                      In neuem Tab
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={closeInlinePdf}
                      className="rounded-none border-[#C4A084] text-[#5D4E3C]"
                      data-testid="docsettings-pdf-close"
                    >
                      <X className="h-4 w-4 mr-1" /> PDF schließen
                    </Button>
                  </div>
                </div>
                <iframe
                  src={realPdfUrl}
                  title="Live-PDF-Vorschau"
                  className="w-full bg-white border border-[#E8DCC8] rounded-none"
                  style={{ height: 'min(85vh, 1100px)', minHeight: 600 }}
                  data-testid="docsettings-pdf-iframe"
                />
                <p className="text-xs text-[#8B7355] italic">
                  Hinweis: Diese Vorschau spiegelt das echte PDF wider. Auf iPhone öffnet sich das PDF eventuell in einem neuen Tab — nutze dann „In neuem Tab".
                </p>
              </div>
            ) : (
              getPreviewContent()
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <PreviewModal />
      <div className="max-w-7xl mx-auto">
        <AdminPageHeader 
          title="Dokument-Einstellungen" 
          subtitle="Firmendaten, Logo und Briefvorlagen verwalten"
          icon={FileText}
        />

        {/* Section: Aktionen - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
              <FileText className="h-4 w-4 text-[#60B060]" />
            </div>
            <h2 className="text-base font-semibold text-white">Schnell-Aktionen</h2>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setPreviewType('invoice');
                setShowPreview(true);
              }}
              className="bg-white text-[#C4A084] hover:bg-white/90 rounded-none"
            >
              <Eye className="h-4 w-4 mr-2" />
              Vorschau
            </Button>
            <Button onClick={handleSaveSettings} disabled={loading} size="sm" className="bg-white text-[#C4A084] hover:bg-white/90 rounded-none">
              <Save className="h-4 w-4 mr-2" />
              Speichern
            </Button>
          </div>
        </div>

        {/* Tabs - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2 mt-4" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#FFF8E0]">
            <FileText className="h-4 w-4 text-[#C0A030]" />
          </div>
          <h2 className="text-base font-semibold text-white">Einstellungs-Bereiche</h2>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <Button
            variant={activeTab === 'company' ? 'default' : 'outline'}
            onClick={() => setActiveTab('company')}
            className="rounded-none"
            style={activeTab === 'company' ? { backgroundColor: '#C4A084' } : {}}
          >
            Firmendaten
          </Button>
          <Button
            variant={activeTab === 'logo' ? 'default' : 'outline'}
            onClick={() => setActiveTab('logo')}
            className="rounded-none"
            style={activeTab === 'logo' ? { backgroundColor: '#C4A084' } : {}}
          >
            Logo
          </Button>
          <Button
            variant={activeTab === 'bank' ? 'default' : 'outline'}
            onClick={() => setActiveTab('bank')}
            className="rounded-none"
            style={activeTab === 'bank' ? { backgroundColor: '#C4A084' } : {}}
          >
            Bankverbindung
          </Button>
          <Button
            variant={activeTab === 'legal' ? 'default' : 'outline'}
            onClick={() => setActiveTab('legal')}
            className="rounded-none"
            style={activeTab === 'legal' ? { backgroundColor: '#C4A084' } : {}}
          >
            Rechtstexte
          </Button>
          <Button
            variant={activeTab === 'letters' ? 'default' : 'outline'}
            onClick={() => setActiveTab('letters')}
            className="rounded-none"
            style={activeTab === 'letters' ? { backgroundColor: '#C4A084' } : {}}
          >
            Briefvorlagen
          </Button>
        </div>

        {/* Company Data Tab */}
        {activeTab === 'company' && (
          <Card>
            <CardHeader>
              <CardTitle>Firmendaten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Firmenname</Label>
                  <Input
                    value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Inhabername</Label>
                  <Input
                    value={settings.owner_name}
                    onChange={(e) => setSettings({ ...settings, owner_name: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Straße & Hausnummer</Label>
                  <Input
                    value={settings.street}
                    onChange={(e) => setSettings({ ...settings, street: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>PLZ</Label>
                    <Input
                      value={settings.postal_code}
                      onChange={(e) => setSettings({ ...settings, postal_code: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Stadt</Label>
                    <Input
                      value={settings.city}
                      onChange={(e) => setSettings({ ...settings, city: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>E-Mail</Label>
                  <Input
                    type="email"
                    value={settings.email}
                    onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input
                    value={settings.website}
                    onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={settings.phone}
                    onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logo Tab */}
        {activeTab === 'logo' && (
          <Card>
            <CardHeader>
              <CardTitle>Firmenlogo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {settings.logo_url && (
                  <div>
                    <Label>Aktuelles Logo</Label>
                    <div className="mt-2 p-4 border rounded bg-white">
                      <img
                        src={settings.logo_url}
                        alt="Company Logo"
                        className="max-w-xs max-h-40 object-contain"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <Label>Logo hochladen</Label>
                  <div className="mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      Empfohlen: PNG oder JPG, quadratisches Format
                    </p>
                  </div>
                </div>
                <div>
                  <Label>Logo-URL (alternativ)</Label>
                  <Input
                    value={settings.logo_url}
                    onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bank Tab */}
        {activeTab === 'bank' && (
          <Card>
            <CardHeader>
              <CardTitle>Bankverbindung</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={settings.bank_name}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="z.B. Sparkasse"
                  />
                </div>
                <div>
                  <Label>Kontoinhaber</Label>
                  <Input
                    value={settings.account_holder}
                    onChange={(e) => setSettings({ ...settings, account_holder: e.target.value })}
                  />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <Input
                    value={settings.iban}
                    onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                    placeholder="DE89 3704 0044 0532 0130 00"
                  />
                </div>
                <div>
                  <Label>BIC</Label>
                  <Input
                    value={settings.bic}
                    onChange={(e) => setSettings({ ...settings, bic: e.target.value })}
                    placeholder="COBADEFFXXX"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Legal Tab */}
        {activeTab === 'legal' && (
          <Card>
            <CardHeader>
              <CardTitle>Rechts- und Zahlungstexte</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Steuerhinweis</Label>
                  <Textarea
                    value={settings.tax_note}
                    onChange={(e) => setSettings({ ...settings, tax_note: e.target.value })}
                    rows={3}
                    placeholder="z.B. Kleinunternehmer gemäß § 19 UStG..."
                  />
                </div>
                <div>
                  <Label>Zahlungsbedingungen</Label>
                  <Textarea
                    value={settings.payment_terms}
                    onChange={(e) => setSettings({ ...settings, payment_terms: e.target.value })}
                    rows={3}
                    placeholder="z.B. Bitte überweisen Sie den Betrag innerhalb von 14 Tagen..."
                  />
                </div>
                <div>
                  <Label>Dankestext</Label>
                  <Input
                    value={settings.thank_you_note}
                    onChange={(e) => setSettings({ ...settings, thank_you_note: e.target.value })}
                    placeholder="z.B. Vielen Dank für Ihren Einkauf!"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Letter Templates Tab */}
        {activeTab === 'letters' && (
          <div className="space-y-4">
            {!isEditingTemplate ? (
              <>
                <Card className="rounded-none border-[#D4C4B0]">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-[#5D4E3C]">Briefvorlagen</CardTitle>
                      <Button onClick={handleCreateTemplate} className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                        <Plus className="h-4 w-4 mr-2" />
                        Neue Vorlage
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {templates.length === 0 ? (
                      <p className="text-[#8B7355]">Keine Vorlagen vorhanden</p>
                    ) : (
                      <div className="space-y-2">
                        {templates.map((template) => (
                          <div
                            key={template.id}
                            className="flex items-center justify-between p-4 border border-[#D4C4B0] rounded-none hover:bg-[#FAF8F5]"
                          >
                            <div>
                              <h3 className="font-semibold text-[#5D4E3C]">{template.name}</h3>
                              <p className="text-sm text-[#8B7355]">{template.subject}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setPreviewType('letter');
                                  setShowPreview(true);
                                }}
                                className="rounded-none bg-[#E3F2FD] text-[#2196F3] hover:bg-[#BBDEFB] border-0"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setSelectedTemplate(template);
                                  setIsEditingTemplate(true);
                                }}
                                className="rounded-none bg-[#FFF3E0] text-[#FF9800] hover:bg-[#FFE0B2] border-0"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleDeleteTemplate(template.id)}
                                className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="rounded-none border-[#D4C4B0]">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-[#5D4E3C]">
                      {selectedTemplate?.id ? 'Vorlage bearbeiten' : 'Neue Vorlage'}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button onClick={() => setIsEditingTemplate(false)} className="rounded-none bg-[#FFEBEE] text-[#E57373] hover:bg-[#FFCDD2] border-0">
                        Abbrechen
                      </Button>
                      <Button onClick={handleSaveTemplate} disabled={loading} className="rounded-none bg-[#E8F5E9] text-[#4CAF50] hover:bg-[#C8E6C9] border-0">
                        <Save className="h-4 w-4 mr-2" />
                        Speichern
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-[#5D4E3C]">Vorlagenname</Label>
                      <Input
                        value={selectedTemplate?.name || ''}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, name: e.target.value })
                        }
                        placeholder="z.B. Standard, Mahnung, Angebot"
                      />
                    </div>
                    <div>
                      <Label>Betreff</Label>
                      <Input
                        value={selectedTemplate?.subject || ''}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, subject: e.target.value })
                        }
                        placeholder="Betreffzeile des Briefs"
                      />
                    </div>
                    <div>
                      <Label>Anrede</Label>
                      <Input
                        value={selectedTemplate?.greeting || ''}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, greeting: e.target.value })
                        }
                        placeholder="Sehr geehrte Damen und Herren,"
                      />
                    </div>
                    <div>
                      <Label>Brieftext</Label>
                      <Textarea
                        value={selectedTemplate?.body || ''}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, body: e.target.value })
                        }
                        rows={10}
                        placeholder="Briefinhalt... Sie können Platzhalter verwenden: {{name}}, {{company}}, {{date}}"
                      />
                      <p className="text-sm text-gray-500 mt-2">
                        Verfügbare Platzhalter: {'{{'} name {'}}'},  {'{{'} company {'}}'},  {'{{'} date {'}}'} 
                      </p>
                    </div>
                    <div>
                      <Label>Grußformel</Label>
                      <Input
                        value={selectedTemplate?.closing || ''}
                        onChange={(e) =>
                          setSelectedTemplate({ ...selectedTemplate, closing: e.target.value })
                        }
                        placeholder="Mit freundlichen Grüßen"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSettings;
